import * as React from 'react';

type UseBarHeightOptions = {
  ref?: React.RefObject<HTMLElement | null>;
  selector?: string;
};

function getHeight(el: Element | null): number {
  if (!el) return 0;
  const rect = (el as HTMLElement).getBoundingClientRect();
  return Math.max(0, Math.round(rect.height));
}

export function useElementHeight(options: UseBarHeightOptions = {}): number {
  const { ref, selector } = options;

  const [height, setHeight] = React.useState<number>(0);

  React.useLayoutEffect(() => {
    const el = ref?.current ?? (selector ? document.querySelector(selector) : null);

    let raf = 0;

    const commit = () => {
      const next = getHeight(el);
      setHeight((prev) => (prev === next ? prev : next));
    };

    const scheduleCommit = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(commit);
    };

    scheduleCommit();

    const ro = new ResizeObserver(scheduleCommit);
    if (el) ro.observe(el);

    window.addEventListener('resize', scheduleCommit);
    window.addEventListener('orientationchange', scheduleCommit);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', scheduleCommit);
      window.removeEventListener('orientationchange', scheduleCommit);
    };
  }, [ref, selector]);

  return height;
}
